/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-09
 */

import { useEffect } from 'react';
import { Alert, AlertTitle, Button, Box } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';

// Success types for categorization
export type SuccessType =
  | 'registration'
  | 'login'
  | 'logout'
  | 'profile'
  | 'password'
  | 'verification'
  | 'general';

// Success alert props interface
interface SuccessAlertProps {
  message: string | null;
  successType?: SuccessType;
  onAction?: () => void;
  onDismiss?: () => void;
  showAction?: boolean;
  actionLabel?: string;
  autoHide?: boolean;
  hideDelay?: number;
}

/**
 * Enhanced success alert component for displaying success messages with categorization and action functionality.
 *
 * @example
 * return (
 *   <SuccessAlert
 *     message="Profile updated successfully!"
 *     successType="profile"
 *     onAction={handleViewProfile}
 *     showAction={true}
 *     actionLabel="View Profile"
 *   />
 * )
 * @param {SuccessAlertProps} props - Component props
 * @returns {React.ReactElement | null} Success alert component or null if no message
 */
const SuccessAlert: React.FC<SuccessAlertProps> = ({
  message,
  successType = 'general',
  onAction,
  onDismiss,
  showAction = false,
  actionLabel = 'Continue',
  autoHide = true,
  hideDelay = 4000,
}) => {
  // Auto-hide functionality
  useEffect(() => {
    if (!autoHide || !onDismiss) return undefined;

    const timer = setTimeout(() => {
      onDismiss();
    }, hideDelay);

    return () => clearTimeout(timer);
  }, [autoHide, hideDelay, onDismiss]);

  if (!message) return null;

  // Get user-friendly title based on success type
  const getTitle = (type: SuccessType) => {
    switch (type) {
      case 'registration':
        return 'Registration Successful';
      case 'login':
        return 'Welcome Back';
      case 'logout':
        return 'Logged Out';
      case 'profile':
        return 'Profile Updated';
      case 'password':
        return 'Password Changed';
      case 'verification':
        return 'Verification Complete';
      default:
        return 'Success';
    }
  };

  const title = getTitle(successType);

  return (
    <Alert
      severity="success"
      onClose={onDismiss}
      icon={<CheckIcon />}
      sx={{
        mb: 2,
        '& .MuiAlert-message': {
          width: '100%',
        },
        '& .MuiAlert-icon': {
          color: 'success.main',
        },
      }}
    >
      <AlertTitle sx={{ fontWeight: 600 }}>{title}</AlertTitle>
      {message}

      {showAction && onAction && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            color="success"
            endIcon={<ArrowIcon />}
            onClick={onAction}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
            }}
          >
            {actionLabel}
          </Button>
        </Box>
      )}
    </Alert>
  );
};

export default SuccessAlert;
